/* eslint-disable @next/next/no-img-element */
import React from 'react';
import { CheckIcon } from '@heroicons/react/outline';
import Image from 'next/legacy/image';

const examples = [
  {
    name: 'Simple',
    imageUrl: '/images/examples/simple.webp',
    demoLink: 'https://nextjs.simple.clerk.app/',
    repoLink:
      'https://github.com/clerkinc/clerk-nextjs-examples/tree/main/examples/simple',
    description: 'Using Clerk Components simply.',
    features: [
      'Email Magic Links',
      'Sign in with Google',
      'MFA w/ SMS',
      'User profile component'
    ]
  },
  {
    name: 'Acme',
    imageUrl: '/images/examples/acme.webp',
    demoLink: 'https://nextjs.acme.clerk.app/',
    repoLink:
      'https://github.com/clerkinc/clerk-nextjs-examples/tree/main/examples/acme',
    description: 'Styling Clerk Components elegantly.',
    features: [
      'Email or SMS Sign in',
      'User button component',
      'CSS in JS styling',
      'User profile component'
    ]
  },
  {
    name: 'Widget',
    imageUrl: '/images/examples/widget.webp',
    demoLink: 'https://nextjs.widget.clerk.app/',
    repoLink:
      'https://github.com/clerkinc/clerk-nextjs-examples/tree/main/examples/widget',
    description: 'Collect custom fields at sign up.',
    features: [
      'Email/password sign in',
      'Styled components',
      'Custom styling',
      'Addt. sign up fields'
    ]
  },
  {
    name: 'Oasis',
    imageUrl: '/images/examples/oasis.webp',
    demoLink: 'https://nextjs.oasis.clerk.app/',
    repoLink:
      'https://github.com/clerkinc/clerk-nextjs-examples/tree/main/examples/oasis',
    description: 'Build a progressive sign up form.',
    features: [
      'Email code sign in',
      'User Profile',
      'Fully customized',
      'Addt. sign up fields'
    ]
  }
];

const addtCSS = `
.codeFont {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
`;

export const ExampleProjs: React.FC = () => (
  <>
    <style>{addtCSS}</style>
    <div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h3 className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            Open source examples
          </h3>
          <p className="mx-auto mt-2 max-w-3xl text-xl text-gray-500">
            See them in action. Then, clone and customize to make them yours.
          </p>
        </div>
      </div>
    </div>
    <div className="mx-auto max-w-4xl px-4 sm:px-10 lg:px-8">
      <div className="mt-12 grid gap-x-12 gap-y-16 lg:grid-cols-2">
        {examples.map(example => (
          <div
            key={example.name}
            className="mx-auto flex max-w-sm flex-col overflow-hidden rounded-lg shadow-lg duration-300 hover:scale-105 hover:shadow-2xl"
          >
            <div className="flex-shrink-0">
              <Image
                src={example.imageUrl}
                alt={example.name}
                width={384}
                height={210}
              />
            </div>
            <div className="flex flex-1 flex-col justify-between bg-white p-6">
              <div className="flex-1">
                <h4 className="text-xl font-semibold text-gray-900">
                  {example.name}
                </h4>
                <p className="text-gray-600">{example.description}</p>
                <h5 className="mt-6 mb-3 text-sm font-medium text-gray-600">
                  Featuring
                </h5>
                <ul className="text-sm text-gray-500">
                  {example.features?.map(feature => (
                    <li key={feature}>
                      <CheckIcon
                        className="absolute h-6 w-6 text-green-500"
                        aria-hidden="true"
                      />
                      <p className="ml-7">{feature}</p>
                    </li>
                  ))}
                </ul>
                <div className="mx-auto mt-6 max-w-md space-x-0 align-middle sm:flex">
                  <a
                    href={example.demoLink}
                    target="blank"
                    rel="noopener"
                    className="inline-flex items-center rounded-md border border-transparent bg-clerk-blue px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-blue-700 focus:outline-none"
                    key={example.name}
                  >
                    <button type="button">Live Demo</button>
                  </a>
                  <a
                    href={example.repoLink}
                    target="blank"
                    rel="noopener"
                    className="inline-flex items-center px-3 py-2 text-sm font-medium leading-4 text-clerk-blue"
                  >
                    View on GitHub
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </>
);
